import type { Match } from "@/lib/types";
import { matchHasReadyVideo } from "@/features/season/seasonHelpers";
import { shootoutOutcome } from "@/features/matches/matchResult";
import { countsTowardRecord } from "@/features/matches/matchCompetition";
type TeamPerformance = {
  matchesPlayed: number;
  goalsFor: number;
  goalsAgainst: number;
  cleanSheets: number;
  replaysAvailable: number;
  teamMatchCount: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalDiff: number;
  /** Shootouts won / lost among the drawn matches (0051). These are a
   * SUBSET of `draws`, not a separate result bucket: a match decided on
   * penalties still records as a draw worth one point, per football
   * convention. Reported beside the record so a coach can see how the
   * knockouts actually went without the points total lying. */
  pkWins: number;
  pkLosses: number;
  /** Scored LEAGUE matches only — the denominator for any per-match
   * average of a league-only stat (0052). `matchesPlayed` counts
   * tournament and friendly fixtures too, so dividing goalsFor or
   * points by it mixes two scopes and understates both. */
  leagueMatchesPlayed: number;
};

/** Classify which side of a match this team played on. Prefers the
 * structured `home_team_id` / `away_team_id` columns (alembic 0004)
 * over the legacy string match — IDs are robust against operator
 * typos / casing drift; the string match is the fallback for rows
 * created before the migration (or for external scrimmages where
 * neither side maps to a tktic tenant). Returns "none" when neither
 * classification places this team. */
export function sideForTeam(
  match: Match,
  teamId: string,
  teamName: string,
): "home" | "away" | "none" {
  if (teamId) {
    if (match.home_team_id && match.home_team_id === teamId) return "home";
    if (match.away_team_id && match.away_team_id === teamId) return "away";
    // Either side has an explicit non-matching id ⇒ trust the data,
    // don't fall through to the string match (the structured signal
    // says this team isn't on either side, e.g. an opponent created
    // the row and tagged THEIR own side).
    if (match.home_team_id || match.away_team_id) {
      // Only short-circuit when at least one side has an explicit id
      // for a different team — that's the cross-tenant case the
      // string fallback would otherwise mis-classify. If BOTH sides
      // are null we still try the string match below.
    }
  }
  const lowerName = teamName.trim().toLowerCase();
  if (!lowerName) return "none";
  // Legacy fallback — applied only when the structured signal didn't
  // place the team explicitly (both columns null, or no team-id
  // available from the caller).
  if (!match.home_team_id && !match.away_team_id) {
    if (match.home_team.trim().toLowerCase() === lowerName) return "home";
    if (match.away_team.trim().toLowerCase() === lowerName) return "away";
  }
  return "none";
}

/** Compute the KPI snapshot for the *visible* match set (filter +
 * search already applied). Mirrors the legacy `renderSeasonTeamStats`
 * math in `js/views.js` of the pre-fork `github.com/humac/replay` repo:
 *   - All KPIs except `replaysAvailable` are gated on having a
 *     recorded score (`score_home` AND `score_away` non-null).
 *   - `replaysAvailable` counts every team-match with a ready video,
 *     scored or not.
 *   - `points = wins*3 + draws`.
 *
 * `teamId` + `teamName` come from the per-team page's resolved
 * `team`. ID classification (alembic 0004) wins; the name fallback
 * preserves legacy behavior for un-migrated rows.
 */
export function computeTeamPerformance(
  visible: Match[],
  teamId: string,
  teamName: string,
): TeamPerformance {
  const out: TeamPerformance = {
    matchesPlayed: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    cleanSheets: 0,
    replaysAvailable: 0,
    teamMatchCount: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    points: 0,
    goalDiff: 0,
    pkWins: 0,
    pkLosses: 0,
    leagueMatchesPlayed: 0,
  };
  if (!teamId && !teamName.trim()) return out;

  for (const m of visible) {
    const side = sideForTeam(m, teamId, teamName);
    if (side === "none") continue;
    const isHome = side === "home";
    out.teamMatchCount += 1;
    if (matchHasReadyVideo(m)) out.replaysAvailable += 1;
    if (
      m.match_status !== "completed" ||
      m.score_home == null ||
      m.score_away == null
    )
      continue;
    out.matchesPlayed += 1;
    // Third exclusion gate (0052), alongside `side === "none"` and the
    // null-score check above. Tournament and friendly fixtures are
    // excluded from the RECORD but deliberately counted in
    // teamMatchCount / replaysAvailable / matchesPlayed above — the
    // video is real and a coach still needs to find it. Placing the
    // gate here rather than at the top of the loop is what produces
    // that split. See `matchCompetition.ts`.
    if (!countsTowardRecord(m)) continue;
    out.leagueMatchesPlayed += 1;
    const ours = isHome ? m.score_home : m.score_away;
    const theirs = isHome ? m.score_away : m.score_home;
    out.goalsFor += ours;
    out.goalsAgainst += theirs;
    out.goalDiff += ours - theirs;
    if (theirs === 0) out.cleanSheets += 1;
    if (ours > theirs) {
      out.wins += 1;
      out.points += 3;
    } else if (ours === theirs) {
      out.draws += 1;
      out.points += 1;
      // A shootout decides who progresses, not the scoreline — the
      // match stays a draw worth one point and the PK goals never
      // touch goalsFor/goalsAgainst/goalDiff/cleanSheets above. Only
      // this counter knows the knockout was settled.
      const pk = shootoutOutcome(m, isHome);
      if (pk === "won") out.pkWins += 1;
      else if (pk === "lost") out.pkLosses += 1;
    } else {
      out.losses += 1;
    }
  }
  return out;
}

/** Result badge uses the viewing team's side; unresolved legacy identity has no badge. */
export function resultForTeam(
  match: Match,
  teamId: string,
  teamName: string,
): "W" | "D" | "L" | null {
  const side = sideForTeam(match, teamId, teamName);
  if (
    match.match_status !== "completed" ||
    side === "none" ||
    match.score_home == null ||
    match.score_away == null ||
    match.score_home < 0 ||
    match.score_away < 0
  )
    return null;
  let home = match.score_home,
    away = match.score_away;
  if (
    home === away &&
    match.competition !== "league" &&
    match.pk_home != null &&
    match.pk_away != null &&
    match.pk_home >= 0 &&
    match.pk_away >= 0 &&
    match.pk_home !== match.pk_away
  ) {
    home = match.pk_home;
    away = match.pk_away;
  }
  const ours = side === "home" ? home : away,
    theirs = side === "home" ? away : home;
  return ours > theirs ? "W" : ours < theirs ? "L" : "D";
}
